import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import PermissionedComponent from '~/components/Common/PermissionedComponent';
import UserSearchWidget from '~/components/Common/UserSearchWidget';
import PromoFeature from '~/components/Promo/PromoFeature';
import ColumnSelector from '~/components/Promo/PromoTester/ColumnSelector';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { toPromoBrand } from '~/consts/promoBrand';
import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import { useExpireVoucherUser } from '~/hooks/apis/promo/useExpireVoucherUser';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { allVoucherUsersQueryKeys, useListAllVoucherUsers } from '~/hooks/apis/promo/useListAllVoucherUsers';
import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';
import { useUpdateVoucherUsersExpiryByIds } from '~/hooks/apis/promo/useUpdateVoucherUsersExpiryByIds';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import usePromoFeatureAvailability from '~/hooks/usePromoFeatureAvailability';
import { usePromoPreferences } from '~/hooks/usePromoPreferences';
import useQuery from '~/hooks/useQuery';

import {
  ListAllVoucherUsersQuery,
  ListVoucherLogsQuery,
  PromoMeta,
  VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER,
  listVouchers,
} from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import { getVoucherToCreditBlockReason } from '~/utils/voucherToCredit';

import CopyableField from '../Common/CopyableField';
import DebugModal from '../DebugModal/DebugModal';

import VoucherLogsTable, { VOUCHER_LOGS_TABLE_DEFAULT_QUERY } from './VoucherLogsTable';
import VoucherRecentRequests from './VoucherRecentRequests';
import VoucherToCreditDialog from './VoucherToCreditDialog';
import VoucherUserBulkActions from './VoucherUserBulkActions';
import { type SelectedVoucherUser } from './voucherUserBulkBatches';
import { getAlignVoucherUserExpiryBlockReason } from './voucherUserExpiry';

type Voucher = definitions['Voucher'];
type VoucherUser = definitions['VoucherUser'];
/** API fields not always on contract `VoucherUser` — order id when auto-assigned from an order. */
type VoucherUserRow = VoucherUser & { fkCauseId?: string; idCauseFk?: string };

type VoucherUserReportingTagDefinition = NonNullable<
  PromoMeta['global_promo_config']
>['voucherUserReportingTagsConfig'][number];

const VOUCHER_USER_STATUS_OPTIONS = [
  { value: 'unused', label: 'Unused vouchers' },
  { value: 'used', label: 'Used vouchers' },
  { value: 'expired', label: 'Include expired vouchers' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];
/** Same roles that can add credit on the user Credits page. */
const VOUCHER_TO_CREDIT_ROLES = [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER];
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;
const THIRTY_DAYS_MS = 30 * ONE_DAY_MS;

function getCreatedAtChipColor(createdAt: string): ChipProps['color'] | null {
  const ageMs = Date.now() - dayjs(createdAt).valueOf();

  if (!Number.isFinite(ageMs) || ageMs < ONE_DAY_MS) {
    return 'success';
  }
  if (ageMs < ONE_WEEK_MS) {
    return 'warning';
  }
  if (ageMs > THIRTY_DAYS_MS) {
    return null;
  }
  return 'default';
}

function getExpiresAtChipColor(expiresAt: string, now: number): ChipProps['color'] | null {
  const expiresAtDate = dayjs(expiresAt);
  if (!expiresAtDate.isValid()) {
    return null;
  }
  if (!expiresAtDate.isAfter(now)) {
    return 'error';
  }
  return expiresAtDate.isBefore(dayjs(now).add(3, 'month')) ? 'warning' : null;
}

function reportingTagOptionsFromPromoMeta(
  config: Array<VoucherUserReportingTagDefinition> | undefined,
): Array<{ value: string; label: string }> {
  if (!config) {
    return [];
  }
  const fromMeta = config?.map((def) => ({
    value: def.tag,
    label: def.short_description?.trim() ? def.short_description : def.tag,
  }));
  return fromMeta;
}

/**
 * URL `include_expired=true` enables; explicit `false` or other values disable.
 * When the param is omitted, uses `defaultWhenOmitted` (standalone voucher lookup uses false;
 * voucher edit embed uses true to preserve legacy always-show-expired behavior).
 */
function initialIncludeExpiredFromSearchParams(search: URLSearchParams, defaultWhenOmitted: boolean): boolean {
  const raw = search.get('include_expired');
  if (raw === 'true') {
    return true;
  }
  if (raw === null) {
    return defaultWhenOmitted;
  }
  return false;
}

/** URL `include_used=false` disables; omitted or other → true (matches list API default). */
function parseIncludeUsedFromSearchParams(search: URLSearchParams): boolean {
  return search.get('include_used') !== 'false';
}

/** URL `only_used=true` enables; omitted or other → false (matches list API default). */
function parseOnlyUsedFromSearchParams(search: URLSearchParams): boolean {
  return search.get('only_used') === 'true';
}

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: true,
  disableColumnMenu: true,
  flex: 1,
};

const voucherUserSortFields = {
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  expiredAt: 'expired_at',
  usedAt: 'used_at',
} as const;

const DEFAULT_VOUCHER_USER_SORT_DIRECTION = 'asc';
const defaultVoucherUserSortModel: GridSortModel = [{ field: 'expiredAt', sort: DEFAULT_VOUCHER_USER_SORT_DIRECTION }];
const voucherUserSortOptions = [
  { field: 'expiredAt', label: 'Expires At' },
  { field: 'createdAt', label: 'Created At' },
  { field: 'updatedAt', label: 'Updated At' },
  { field: 'usedAt', label: 'Used At' },
] as const;

interface Props {
  /** Controlled by the voucher form when it owns the mode selector. */
  userMode?: string;
  onModeLockChange?: (locked: boolean) => void;
  voucherId?: string;
  /** Omit page chrome and avoid syncing filters to the URL (use when embedding on another route). */
  embedded?: boolean;
  /** Default `voucher_user_reporting_tag` when the URL has none (standalone) or for embedded mode. */
  defaultVoucherUserReportingTag?: string;
  /**
   * When `embedded`, voucher users and voucher logs are in accordions; this sets their initial expanded state.
   * Voucher Lookup (standalone) uses the default `true`; voucher auto-assign rule embed uses `false`.
   */
  defaultAccordionsOpen?: boolean;
  /** Show the users section as an accordion on the combined vouchers page. */
  showUsersAccordion?: boolean;
  /** Hide voucher logs (e.g. when embedded on the voucher edit page). */
  hideVoucherLogs?: boolean;
  /** Hide the page footer debug modal. */
  hideFooterDebug?: boolean;
  /** Show per-row expire action and confirmation dialog (voucher detail / edit). */
  showExpireVoucherUserActions?: boolean;
  /** Disable expire actions while parent form is busy. */
  expireVoucherUserActionsDisabled?: boolean;
  /**
   * Initial "Include expired" when the URL has no `include_expired` param.
   * Voucher edit (direct `voucherId` prop, no URL sync) should pass `true` for prior behavior.
   */
  defaultIncludeExpired?: boolean;
  /**
   * When `onPromoRequestsUserFilterChange` is set, user ID filter is owned by the parent (voucher edit page)
   * so promo requests render once below the fold instead of duplicating inside the users section.
   */
  promoRequestsUserFilter?: string;
  onPromoRequestsUserFilterChange?: (userId: string) => void;
}

export default function VoucherUserLookup({
  userMode: controlledUserMode,
  onModeLockChange,
  voucherId: propVoucherId,
  embedded = false,
  defaultVoucherUserReportingTag = '',
  defaultAccordionsOpen = true,
  showUsersAccordion = false,
  hideVoucherLogs = false,
  hideFooterDebug = false,
  showExpireVoucherUserActions = true,
  expireVoucherUserActionsDisabled = false,
  defaultIncludeExpired = false,
  promoRequestsUserFilter,
  onPromoRequestsUserFilterChange,
}: Props = {}) {
  const { hash } = useLocation();
  const query = useQuery();
  const adminUserIdFromQuery = query.get('admin_user_id')?.trim() ?? '';
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { replace } = useAppNavigation();
  const tenant = useCurrentTenant();
  const { promoMeta } = usePromoMeta();
  const voucherIdFromQuery = embedded ? '' : (query.get('voucher_id') ?? '');
  const hasDirectVoucherId = !!propVoucherId || !!voucherIdFromQuery;
  const isPromoUserFilterLinked = Boolean(onPromoRequestsUserFilterChange);

  const initialReportingTag = embedded
    ? defaultVoucherUserReportingTag || ''
    : query.get('voucher_user_reporting_tag') || defaultVoucherUserReportingTag || '';

  const [pageNum, setPageNum] = useState<number>(
    embedded ? 0 : query.get('page_num') ? Number(query.get('page_num')) : 0,
  );
  const [pageSize, setPageSize] = useState<number>(
    embedded ? 20 : query.get('page_size') ? Number(query.get('page_size')) : 20,
  );
  const [filterUserIdInternal, setFilterUserIdInternal] = useState<string>(embedded ? '' : query.get('user_id') || '');
  const filterUserId = isPromoUserFilterLinked ? (promoRequestsUserFilter ?? '') : filterUserIdInternal;
  const [filterVoucherName, setFilterVoucherName] = useState<string>(embedded ? '' : query.get('voucher_name') || '');
  const [filterReportingTags, setFilterReportingTags] = useState<Array<string>>(() => {
    const tags = embedded ? [] : query.getAll('reporting_tags');
    return tags.length ? tags : initialReportingTag ? [initialReportingTag] : [];
  });
  const [filterExcludedReportingTags, setFilterExcludedReportingTags] = useState<Array<string>>(
    embedded ? [] : query.getAll('excluded_reporting_tags'),
  );
  const [filterExpiresAfterVoucher, setFilterExpiresAfterVoucher] = useState(
    !embedded && query.get('expires_after_voucher') === 'true',
  );
  const [filterCreatedBy, setFilterCreatedBy] = useState(embedded ? '' : query.get('created_by') || '');
  const [filterUpdatedAfter, setFilterUpdatedAfter] = useState(embedded ? '' : query.get('updated_after') || '');
  const [filterUpdatedBefore, setFilterUpdatedBefore] = useState(embedded ? '' : query.get('updated_before') || '');
  const [selectedMatchedVoucherId, setSelectedMatchedVoucherId] = useState<string | undefined>(undefined);
  const [searchWidgetValue, setSearchWidgetValue] = useState<string>(embedded ? '' : query.get('user_id') || '');
  const [filterIncludeExpired, setFilterIncludeExpired] = useState<boolean>(() =>
    embedded ? defaultIncludeExpired : initialIncludeExpiredFromSearchParams(query, defaultIncludeExpired),
  );
  const [filterIncludeUsed, setFilterIncludeUsed] = useState<boolean>(() =>
    embedded ? true : parseIncludeUsedFromSearchParams(query),
  );
  const [filterOnlyUsed, setFilterOnlyUsed] = useState<boolean>(() =>
    embedded ? false : parseOnlyUsedFromSearchParams(query),
  );

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const selectedStatusOptions = [
    ...(!filterOnlyUsed ? ['unused'] : []),
    ...(filterOnlyUsed || filterIncludeUsed ? ['used'] : []),
    ...(filterIncludeExpired ? ['expired'] : []),
  ];

  const [expiryResolutionTarget, setExpiryResolutionTarget] = useState<{
    row: VoucherUserRow;
    expiredAt: string;
  } | null>(null);
  const updateExpiryMutation = useUpdateVoucherUsersExpiryByIds();
  const [expireTarget, setExpireTarget] = useState<VoucherUserRow | null>(null);
  const [creditTarget, setCreditTarget] = useState<VoucherUserRow | null>(null);
  // Assignments that already got credit in this session, so the button is disabled if removing the voucher failed.
  // This is only a UI hint: the fixed transaction key on the credit is what stops a second grant.
  const [creditedVoucherUserIds, setCreditedVoucherUserIds] = useState<ReadonlySet<string>>(() => new Set());
  const [localUserMode, setLocalUserMode] = useState('read-only');
  const userMode = controlledUserMode ?? localUserMode;
  const canEditVoucherUsers = usePromoFeatureAvailability('voucher');
  const isEditing = canEditVoucherUsers && !expireVoucherUserActionsDisabled && userMode !== 'read-only';
  const [selectedVoucherUsers, setSelectedVoucherUsers] = useState<Array<SelectedVoucherUser>>([]);
  const [bulkSelectionLocked, setBulkSelectionLocked] = useState(false);
  const canBulkRemoveVoucherUsers = usePromoFeatureAvailability('voucher-user-bulk-remove');
  const bulkUpdateMode = isEditing && userMode === 'bulk-edit' && canBulkRemoveVoucherUsers;

  useEffect(() => {
    if (!bulkUpdateMode) {
      setSelectedVoucherUsers([]);
    }
  }, [bulkUpdateMode]);
  const expireVoucherUserMutation = useExpireVoucherUser();
  const modeLocked =
    bulkSelectionLocked ||
    expireVoucherUserMutation.isPending ||
    Boolean(creditTarget) ||
    Boolean(expiryResolutionTarget);

  useEffect(() => {
    onModeLockChange?.(modeLocked);
  }, [modeLocked, onModeLockChange]);

  const reportingTagMetaOptions = useMemo(
    () => reportingTagOptionsFromPromoMeta(promoMeta?.global_promo_config?.voucherUserReportingTagsConfig),
    [promoMeta?.global_promo_config?.voucherUserReportingTagsConfig],
  );

  /** Tag string → admin label from promo meta (`short_description`), when configured. */
  const voucherUserReportingTagLabelByTag = useMemo(() => {
    const map = new Map<string, string>();
    reportingTagMetaOptions.forEach((o) => {
      map.set(o.value, o.label);
    });
    return map;
  }, [reportingTagMetaOptions]);

  const handleReportingTagClick = useCallback((tag: string) => {
    setFilterReportingTags((tags) => (tags.includes(tag) ? tags : [...tags, tag]));
    setPageNum(0);
  }, []);

  const handleReportingTagsFilterModeToggle = useCallback(() => {
    if (filterReportingTags.length > 0 && filterExcludedReportingTags.length === 0) {
      setFilterExcludedReportingTags(filterReportingTags);
      setFilterReportingTags([]);
    } else if (filterExcludedReportingTags.length > 0 && filterReportingTags.length === 0) {
      setFilterReportingTags(filterExcludedReportingTags);
      setFilterExcludedReportingTags([]);
    }
    setPageNum(0);
  }, [filterReportingTags, filterExcludedReportingTags]);

  const reportingTagsFilterModeLabel = useMemo(() => {
    const tags = filterReportingTags.length > 0 ? filterReportingTags : filterExcludedReportingTags;
    if (!tags.length || (filterReportingTags.length > 0 && filterExcludedReportingTags.length > 0)) {
      return undefined;
    }
    const label = tags.map((tag) => voucherUserReportingTagLabelByTag.get(tag) ?? tag).join(', ');
    return filterReportingTags.length > 0 ? `Exclude ${label}` : `Only ${label}`;
  }, [filterReportingTags, filterExcludedReportingTags, voucherUserReportingTagLabelByTag]);

  const updateFilterUserId = useCallback(
    (next: string) => {
      if (isPromoUserFilterLinked) {
        onPromoRequestsUserFilterChange(next);
      } else {
        setFilterUserIdInternal(next);
      }
    },
    [isPromoUserFilterLinked, onPromoRequestsUserFilterChange],
  );

  useEffect(() => {
    if (isPromoUserFilterLinked) {
      setSearchWidgetValue(promoRequestsUserFilter ?? '');
    }
  }, [isPromoUserFilterLinked, promoRequestsUserFilter]);

  const handleUserSelected = useCallback(
    (_id: string, value: string) => {
      updateFilterUserId(value);
      setPageNum(0);
    },
    [updateFilterUserId],
  );

  const handleUserIdClick = useCallback(
    (userId: string) => {
      updateFilterUserId(userId);
      setSearchWidgetValue(userId);
      setPageNum(0);
    },
    [updateFilterUserId],
  );

  const handleVoucherClick = useCallback((voucherName: string) => {
    setFilterVoucherName(voucherName);
    setSelectedMatchedVoucherId(undefined);
    setPageNum(0);
  }, []);

  const handleClear = useCallback(() => {
    updateFilterUserId('');
    setFilterVoucherName('');
    setFilterReportingTags(embedded && defaultVoucherUserReportingTag ? [defaultVoucherUserReportingTag] : []);
    setFilterExcludedReportingTags([]);
    setFilterExpiresAfterVoucher(false);
    setFilterCreatedBy('');
    setFilterUpdatedAfter('');
    setFilterUpdatedBefore('');
    setSelectedMatchedVoucherId(undefined);
    setSearchWidgetValue('');
    setFilterIncludeExpired(false);
    setFilterIncludeUsed(true);
    setFilterOnlyUsed(false);
    setPageNum(0);
    if (!propVoucherId && !embedded) {
      replace({
        search: qs.stringify({
          page_num: '0',
          page_size: pageSize.toString(),
        }),
        hash,
      });
    }
  }, [defaultVoucherUserReportingTag, embedded, hash, replace, pageSize, propVoucherId, updateFilterUserId]);

  // Name-based voucher lookup — skipped when a direct voucherId is provided
  const { data: matchedVouchersData } = useTanstackQuery({
    queryKey: ['vouchers', 'name-lookup', filterVoucherName, tenant.tenant.brand],
    queryFn: async () =>
      listVouchers({ voucher_name: filterVoucherName, limit: 100, brand: toPromoBrand(tenant.tenant.brand) }),
    enabled: !hasDirectVoucherId && filterVoucherName.length >= 2,
  });

  const matchedVoucherIds = useMemo<Array<string>>(() => {
    if (hasDirectVoucherId || !filterVoucherName || !matchedVouchersData?.result?.vouchers) {
      return [];
    }
    return matchedVouchersData.result.vouchers.map((v) => v.voucherId);
  }, [hasDirectVoucherId, filterVoucherName, matchedVouchersData]);

  const isVoucherNameFilterActive = !hasDirectVoucherId && filterVoucherName.length >= 2;
  const hasResolvedVouchers = matchedVoucherIds.length > 0;
  const isMultiVoucherMatch = !hasDirectVoucherId && matchedVoucherIds.length > 1;
  const hasSelectedFromMultiMatch =
    isMultiVoucherMatch && !!selectedMatchedVoucherId && matchedVoucherIds.includes(selectedMatchedVoucherId);
  const needsDisambiguation = isMultiVoucherMatch && !hasSelectedFromMultiMatch;

  const resolvedVoucherId =
    propVoucherId ??
    (voucherIdFromQuery ? voucherIdFromQuery : undefined) ??
    selectedMatchedVoucherId ??
    (matchedVoucherIds.length === 1 ? matchedVoucherIds[0] : undefined);

  const resolvedVoucher = useMemo<Voucher | undefined>(() => {
    if (!resolvedVoucherId || hasDirectVoucherId) {
      return undefined;
    }
    return matchedVouchersData?.result?.vouchers?.find((v) => v.voucherId === resolvedVoucherId);
  }, [resolvedVoucherId, hasDirectVoucherId, matchedVouchersData]);

  const brand = useCurrentPromoBrand({
    failMode: 'error',
  });
  const [promoPreferences, setPromoPreferences] = usePromoPreferences();
  const voucherUserPreferences = promoPreferences.uiSelection.voucherUsers;
  const showDateTimes = voucherUserPreferences.showDateTimes ?? false;
  const extendedInfo = voucherUserPreferences.extendedInfo ?? false;
  const sortModel = voucherUserPreferences.sortModel;

  const queryParams: ListAllVoucherUsersQuery = useMemo(() => {
    const activeSort = sortModel[0];
    const sortBy = activeSort && voucherUserSortFields[activeSort.field as keyof typeof voucherUserSortFields];

    return {
      page: pageNum,
      limit: pageSize,
      brand: brand.promoBrand,
      includeExpired: filterIncludeExpired,
      ...(sortBy ? { sortBy, sortByDirection: activeSort.sort ?? DEFAULT_VOUCHER_USER_SORT_DIRECTION } : {}),
      ...(filterOnlyUsed ? {} : { includeUsed: filterIncludeUsed }),
      ...(filterOnlyUsed ? { onlyUsed: true } : {}),
      ...(filterUserId ? { user_id: filterUserId } : {}),
      ...(resolvedVoucherId ? { voucher_id: resolvedVoucherId } : {}),
      voucherUserReportingTags: filterReportingTags.length ? filterReportingTags : undefined,
      voucherUserReportingTagsExclude: filterExcludedReportingTags.length ? filterExcludedReportingTags : undefined,
      isExpiringAfterVoucherExpiryAt: filterExpiresAfterVoucher ? true : undefined,
      createdBy: filterCreatedBy || undefined,
      updatedAfter:
        filterUpdatedAfter && dayjs(filterUpdatedAfter).isValid() ? dayjs(filterUpdatedAfter).toISOString() : undefined,
      updatedBefore:
        filterUpdatedBefore && dayjs(filterUpdatedBefore).isValid()
          ? dayjs(filterUpdatedBefore).toISOString()
          : undefined,
    } satisfies ListAllVoucherUsersQuery;
  }, [
    pageNum,
    pageSize,
    brand.promoBrand,
    filterIncludeExpired,
    filterIncludeUsed,
    filterOnlyUsed,
    filterUserId,
    resolvedVoucherId,
    filterReportingTags,
    filterExcludedReportingTags,
    filterExpiresAfterVoucher,
    filterCreatedBy,
    filterUpdatedAfter,
    filterUpdatedBefore,
    sortModel,
  ]);

  const {
    data: queryData,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
  } = useListAllVoucherUsers({
    query: queryParams,
    enabled:
      hasDirectVoucherId ||
      !isVoucherNameFilterActive ||
      (hasResolvedVouchers && !needsDisambiguation) ||
      !filterVoucherName,
  });

  const voucherUsers = useMemo(() => {
    if (isVoucherNameFilterActive && (!hasResolvedVouchers || needsDisambiguation)) {
      return [];
    }
    return queryData?.result?.voucherUsers ?? [];
  }, [isVoucherNameFilterActive, hasResolvedVouchers, needsDisambiguation, queryData]);

  const selectedVoucherUserIds = useMemo(
    () => new Set(selectedVoucherUsers.map((user) => user.voucherUserId)),
    [selectedVoucherUsers],
  );
  const visibleVoucherUserIds = voucherUsers.map((user) => user.voucherUserId);
  const selectedVisibleCount = visibleVoucherUserIds.filter((id) => selectedVoucherUserIds.has(id)).length;
  const allVisibleSelected = voucherUsers.length > 0 && selectedVisibleCount === voucherUsers.length;
  const toggleVisibleVoucherUsers = (checked: boolean) => {
    setSelectedVoucherUsers((current) => {
      const visibleIds = new Set(visibleVoucherUserIds);
      return checked
        ? [
            ...current.filter((user) => !visibleIds.has(user.voucherUserId)),
            ...voucherUsers.map(({ voucherUserId, voucherId, userId }) => ({ voucherUserId, voucherId, userId })),
          ]
        : current.filter((user) => !visibleIds.has(user.voucherUserId));
    });
  };
  const toggleVoucherUser = (row: VoucherUserRow, checked: boolean) => {
    setSelectedVoucherUsers((current) =>
      checked
        ? [
            ...current.filter((user) => user.voucherUserId !== row.voucherUserId),
            { voucherUserId: row.voucherUserId, voucherId: row.voucherId, userId: row.userId },
          ]
        : current.filter((user) => user.voucherUserId !== row.voucherUserId),
    );
  };

  const totalCount = useMemo(() => {
    if (isVoucherNameFilterActive && (!hasResolvedVouchers || needsDisambiguation)) {
      return 0;
    }
    return Number(queryData?.result?.total ?? 0);
  }, [isVoucherNameFilterActive, hasResolvedVouchers, needsDisambiguation, queryData]);

  const uniqueUserIds = useMemo(() => {
    return Array.from(new Set(voucherUsers.map((vu) => vu.userId).filter(Boolean))).sort();
  }, [voucherUsers]);

  const { data: usersSummaryMap } = useGetUsersSummaryByIds({
    userIds: uniqueUserIds,
    brand: brand.brand,
  });
  const resolvedSummaryMap = usersSummaryMap ?? new Map();

  const uniqueVoucherIds = useMemo(() => {
    return Array.from(
      new Set(
        [...(resolvedVoucherId ? [resolvedVoucherId] : []), ...voucherUsers.map((vu) => vu.voucherId)].filter(Boolean),
      ),
    ).sort();
  }, [resolvedVoucherId, voucherUsers]);

  const { data: vouchersMap } = useGetVouchersByIds({ voucherIds: uniqueVoucherIds, brand: tenant.tenant.brand });
  const resolvedVouchersMap = vouchersMap ?? new Map();

  const syncQueryParams = useCallback(() => {
    if (hasDirectVoucherId || embedded || (showUsersAccordion && hash !== '#voucher-users')) {
      return;
    }
    const searchParams = qs.stringify(
      {
        page_num: pageNum.toString(),
        page_size: pageSize.toString(),
        user_id: filterUserId || undefined,
        admin_user_id: adminUserIdFromQuery || undefined,
        voucher_name: filterVoucherName || undefined,
        reporting_tags: filterReportingTags,
        excluded_reporting_tags: filterExcludedReportingTags,
        expires_after_voucher: filterExpiresAfterVoucher ? 'true' : undefined,
        created_by: filterCreatedBy || undefined,
        updated_after: filterUpdatedAfter || undefined,
        updated_before: filterUpdatedBefore || undefined,
        ...(filterIncludeExpired ? { include_expired: 'true' } : {}),
        ...(!filterIncludeUsed && !filterOnlyUsed ? { include_used: 'false' } : {}),
        ...(filterOnlyUsed ? { only_used: 'true' } : {}),
      },
      { arrayFormat: 'repeat' },
    );
    replace({ search: `?${searchParams}`, hash });
  }, [
    adminUserIdFromQuery,
    embedded,
    filterIncludeExpired,
    filterIncludeUsed,
    filterOnlyUsed,
    hasDirectVoucherId,
    hash,
    replace,
    pageNum,
    pageSize,
    filterUserId,
    filterVoucherName,
    filterReportingTags,
    filterExcludedReportingTags,
    filterExpiresAfterVoucher,
    filterCreatedBy,
    filterUpdatedAfter,
    filterUpdatedBefore,
    showUsersAccordion,
  ]);

  useEffect(() => {
    syncQueryParams();
  }, [syncQueryParams]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const [voucherLogsQuery, setVoucherLogsQuery] = useState<ListVoucherLogsQuery>({});

  const firstMatchedVoucherId = useMemo(() => {
    if (propVoucherId) {
      return propVoucherId;
    }
    if (voucherIdFromQuery) {
      return voucherIdFromQuery;
    }
    if (selectedMatchedVoucherId) {
      return selectedMatchedVoucherId;
    }
    if (!filterVoucherName || matchedVoucherIds.length === 0) {
      return undefined;
    }
    return matchedVoucherIds[0];
  }, [propVoucherId, voucherIdFromQuery, selectedMatchedVoucherId, filterVoucherName, matchedVoucherIds]);

  const voucherLogsQueryOptions = useMemo<ListVoucherLogsQuery>(
    () => ({
      ...voucherLogsQuery,
      ...(filterUserId ? { userId: filterUserId } : {}),
      ...(firstMatchedVoucherId ? { voucherId: firstMatchedVoucherId } : {}),
      ...(brand.promoBrand ? { brand: brand.promoBrand } : { brand: 'luxuryescapes' }),
    }),
    [voucherLogsQuery, filterUserId, firstMatchedVoucherId, brand],
  );

  const voucherLogsListQueryResolved = useMemo<ListVoucherLogsQuery>(
    () => ({
      ...VOUCHER_LOGS_TABLE_DEFAULT_QUERY,
      ...voucherLogsQueryOptions,
    }),
    [voucherLogsQueryOptions],
  );

  const { data: voucherLogsListSummary } = useListVoucherLogs({
    query: voucherLogsListQueryResolved,
    enabled: !hideVoucherLogs,
  });

  const voucherLogsTotalCount = Number(voucherLogsListSummary?.result?.total ?? 0);
  const voucherLogsPageRowCount = voucherLogsListSummary?.result?.logs?.length ?? 0;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    userId: false,
    updatedAt: false,
    voucherUserId: false,
    brand: false,
    createdBy: false,
    fkCauseId: false,
  });
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentTime(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  const displayedSortModel = sortModel.length > 0 ? sortModel : defaultVoucherUserSortModel;

  useEffect(() => {
    setColumnVisibilityModel((current) => ({ ...current, ...voucherUserPreferences.columnVisibility }));
  }, [voucherUserPreferences.columnVisibility]);

  const handleColumnVisibilityChange = useCallback(
    (field: string, visible: boolean) => {
      setColumnVisibilityModel((prev) => ({ ...prev, [field]: visible }));
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherUsers: {
            ...current.uiSelection.voucherUsers,
            columnVisibility: { ...current.uiSelection.voucherUsers.columnVisibility, [field]: visible },
          },
        },
      }));
    },
    [setPromoPreferences],
  );

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherUsers: { ...current.uiSelection.voucherUsers, sortModel: nextSortModel },
        },
      }));
    },
    [setPromoPreferences],
  );

  const handleSortFieldChange = useCallback(
    (field: string) => {
      handleSortModelChange([{ field, sort: displayedSortModel[0]?.sort ?? DEFAULT_VOUCHER_USER_SORT_DIRECTION }]);
    },
    [displayedSortModel, handleSortModelChange],
  );

  const handleSortDirectionChange = useCallback(
    (sort: 'asc' | 'desc') => {
      handleSortModelChange([{ field: displayedSortModel[0]?.field ?? 'expiredAt', sort }]);
    },
    [displayedSortModel, handleSortModelChange],
  );

  const handleExtendedInfoChange = useCallback(
    (checked: boolean) => {
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherUsers: { ...current.uiSelection.voucherUsers, extendedInfo: checked },
        },
      }));
    },
    [setPromoPreferences],
  );

  const shouldShowDeleteVoucherUserActions = isEditing && showExpireVoucherUserActions;

  const mergedColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      ...columnVisibilityModel,
      debug: extendedInfo,
      ...(shouldShowDeleteVoucherUserActions
        ? { expireActions: true, deleteAction: true, alignExpiryAction: true }
        : {}),
    }),
    [columnVisibilityModel, extendedInfo, shouldShowDeleteVoucherUserActions],
  );

  const handleGridColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const { debug: _d, expireActions: _e, deleteAction: _delete, alignExpiryAction: _align, ...rest } = model;
      setColumnVisibilityModel(rest);
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherUsers: {
            ...current.uiSelection.voucherUsers,
            columnVisibility: {
              ...current.uiSelection.voucherUsers.columnVisibility,
              ...rest,
            },
          },
        },
      }));
    },
    [setPromoPreferences],
  );

  const refreshVoucherUsers = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
    if (resolvedVoucherId) {
      queryClient.invalidateQueries({ queryKey: ['voucher-users', resolvedVoucherId] });
    }
  }, [queryClient, resolvedVoucherId]);

  const handleResolveExpiry = async () => {
    if (
      !expiryResolutionTarget ||
      !isEditing ||
      Boolean(expiryResolutionTarget.row.usedAt) ||
      !brand.promoBrand ||
      !dayjs(expiryResolutionTarget.expiredAt).isAfter(dayjs())
    )
      return;
    try {
      const response = await updateExpiryMutation.mutateAsync({
        brand: brand.promoBrand,
        voucherUserIds: [expiryResolutionTarget.row.voucherUserId],
        bulkUpdatePayload: { expiredAt: expiryResolutionTarget.expiredAt },
        dryRun: false,
      });
      if (!response.result.updatedIds.includes(expiryResolutionTarget.row.voucherUserId)) {
        throw new Error('This assignment could not be updated. Refresh and review its current status.');
      }
      enqueueSnackbar('Voucher user expiry updated', { variant: 'success' });
      setExpiryResolutionTarget(null);
      refreshVoucherUsers();
      await queryClient.invalidateQueries({ queryKey: ['vouchers', 'extended-warnings'] });
    } catch (error) {
      enqueueSnackbar(`Failed to update voucher user expiry: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const handleExpireConfirm = useCallback(async () => {
    if (!expireTarget || !isEditing) {
      return;
    }
    try {
      await expireVoucherUserMutation.mutateAsync(expireTarget.voucherUserId);
      enqueueSnackbar('Deleted voucher user', { variant: 'success' });
      refreshVoucherUsers();
    } catch (error) {
      enqueueSnackbar(`Failed to delete voucher user: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setExpireTarget(null);
    }
  }, [enqueueSnackbar, expireTarget, expireVoucherUserMutation, refreshVoucherUsers, isEditing]);

  const voucherUsersSearchPageDebugData = useMemo(
    () => ({
      queryParams,
      resolvedVoucherId,
      filterUserId: filterUserId || undefined,
      filterVoucherName: filterVoucherName || undefined,
      filterReportingTags,
      filterExcludedReportingTags,
      filterExpiresAfterVoucher,
      filterCreatedBy,
      filterUpdatedAfter,
      filterUpdatedBefore,
      totalCount,
      voucherUsers,
      listAllVoucherUsersResponse: queryData,
      tenant: tenant.tenant,
      hasDirectVoucherId,
      needsDisambiguation,
    }),
    [
      filterReportingTags,
      filterExcludedReportingTags,
      filterExpiresAfterVoucher,
      filterCreatedBy,
      filterUpdatedAfter,
      filterUpdatedBefore,
      filterUserId,
      filterVoucherName,
      hasDirectVoucherId,
      needsDisambiguation,
      queryData,
      queryParams,
      resolvedVoucherId,
      tenant.tenant,
      totalCount,
      voucherUsers,
    ],
  );

  const expireTargetUserName = expireTarget
    ? (resolvedSummaryMap.get(expireTarget.userId)?.full_name ?? expireTarget.userId)
    : '';
  const expireTargetVoucherName = expireTarget
    ? (resolvedVouchersMap.get(expireTarget.voucherId)?.voucherName ?? expireTarget.voucherId)
    : '';

  const creditTargetVoucher = creditTarget ? resolvedVouchersMap.get(creditTarget.voucherId) : undefined;
  const creditTargetUserSummary = creditTarget ? resolvedSummaryMap.get(creditTarget.userId) : undefined;

  const columns = useMemo<Array<GridColDef<VoucherUserRow>>>(() => {
    const base: Array<GridColDef<VoucherUserRow>> = [
      ...(bulkUpdateMode
        ? [
            {
              field: 'bulkSelect',
              headerName: 'Select',
              width: 72,
              sortable: false,
              filterable: false,
              hideable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <Checkbox
                  size="small"
                  inputProps={{ 'aria-label': 'Select all voucher users on this page' }}
                  checked={allVisibleSelected}
                  indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                  disabled={bulkSelectionLocked || isLoading || voucherUsers.length === 0}
                  onChange={(_, checked) => toggleVisibleVoucherUsers(checked)}
                />
              ),
              renderCell: (params: { row: VoucherUserRow }) => (
                <Checkbox
                  size="small"
                  inputProps={{ 'aria-label': `Select voucher user ${params.row.voucherUserId}` }}
                  checked={selectedVoucherUserIds.has(params.row.voucherUserId)}
                  disabled={bulkSelectionLocked || isLoading}
                  onChange={(_, checked) => toggleVoucherUser(params.row, checked)}
                />
              ),
            } satisfies GridColDef<VoucherUserRow>,
          ]
        : []),
      {
        field: 'userName',
        headerName: 'Name',
        ...sharedOpts,
        renderCell: (params) => (
          <CopyableField
            value={params.row.userId}
            tooltip="Copy voucher user ID"
            sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}
            label={
              <Stack spacing={0.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                <Link to={`/users/${params.row.userId}`} target="_blank" rel="noreferrer">
                  {resolvedSummaryMap.get(params.row.userId)?.full_name ?? '-'}
                </Link>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {resolvedSummaryMap.get(params.row.userId)?.email ?? '-'}
                </Typography>
              </Stack>
            }
          />
        ),
      },
      {
        field: 'userId',
        headerName: 'User ID',
        ...sharedOpts,
        renderCell: (params) => (
          <Button
            variant="text"
            size="small"
            onClick={() => handleUserIdClick(params.value)}
            sx={{ textTransform: 'none' }}
          >
            {params.value}
          </Button>
        ),
      },
      {
        field: 'voucherId',
        headerName: 'Voucher',
        ...sharedOpts,
        renderCell: (params) => {
          const voucher = resolvedVouchersMap.get(params.row.voucherId);
          const voucherName = voucher?.voucherName ?? params.row.voucherId;
          return (
            <CopyableField
              value={voucherName}
              label={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleVoucherClick(voucher?.voucherName ?? '')}
                  sx={{ textTransform: 'none' }}
                >
                  {voucherName}
                </Button>
              }
            />
          );
        },
      },
      ...(['voucherUserId', 'brand', 'createdBy'] as const).map(
        (field) =>
          ({
            field,
            headerName: { voucherUserId: 'Assignment ID', brand: 'Brand', createdBy: 'Created By' }[field],
            ...sharedOpts,
            minWidth: field === 'brand' ? 130 : 200,
            renderCell: (params) => (params.value ? <CopyableField value={params.value} /> : '—'),
          }) satisfies GridColDef<VoucherUserRow>,
      ),
      {
        field: 'amountUsed',
        headerName: 'Amount Used',
        ...sharedOpts,
        minWidth: 120,
        renderCell: (params) => params.value ?? '—',
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => {
          const color = getCreatedAtChipColor(params.value);
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DateFormatterDetailed date={params.value} includeTime={showDateTimes} />
              {color && <Chip label={dayjs(params.value).fromNow()} color={color} size="small" variant="outlined" />}
            </Stack>
          );
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => {
          const color = getCreatedAtChipColor(params.value);
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DateFormatterDetailed date={params.value} includeTime={showDateTimes} />
              {color && <Chip label={dayjs(params.value).fromNow()} color={color} size="small" variant="outlined" />}
            </Stack>
          );
        },
      },
      {
        field: 'usedAt',
        headerName: 'Used At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) =>
          params.value
            ? DateFormatterDetailed({ date: params.value, includeYear: true, includeTime: showDateTimes })
            : '-',
      },
      {
        field: 'expiredAt',
        headerName: 'Expires At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => {
          if (!params.value) {
            return '-';
          }

          const chipColor = getExpiresAtChipColor(params.value, currentTime);

          return (
            <Stack spacing={0.5} alignItems="center">
              {chipColor ? (
                <Chip label={dayjs(params.value).fromNow()} color={chipColor} size="small" variant="outlined" />
              ) : (
                <Typography variant="body2">{dayjs(params.value).fromNow()}</Typography>
              )}
              <DateFormatterDetailed date={params.value} highlightIfPast includeYear includeTime={showDateTimes} />
            </Stack>
          );
        },
      },
      {
        field: 'expiryWarning',
        headerName: 'Expiry warning',
        ...sharedOpts,
        minWidth: 250,
        renderCell: ({ row }) => {
          const configuredExpiry = resolvedVouchersMap.get(row.voucherId)?.voucherUserExpiresAt;
          if (!row.expiredAt || !configuredExpiry || !dayjs(row.expiredAt).isAfter(dayjs(configuredExpiry))) {
            return null;
          }
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={`Configured user expiry: ${dayjs(configuredExpiry).format('D MMM YYYY, h:mm a')}`}>
                <Chip label="Expiry exceeds voucher" color="warning" size="small" />
              </Tooltip>
              {isEditing && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  disabled={modeLocked || !brand.promoBrand || Boolean(row.usedAt)}
                  onClick={() => setExpiryResolutionTarget({ row, expiredAt: configuredExpiry })}
                >
                  Resolve
                </Button>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'voucherUserReportingTags',
        headerName: 'Reporting Tags',
        ...sharedOpts,
        minWidth: 220,
        flex: 1,
        renderCell: (params) => {
          const tags = params.row.voucherUserReportingTags;
          if (!tags?.length) {
            return '—';
          }
          return (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ py: 0.5, justifyContent: 'center' }}>
              {tags.map((tag) => {
                const reportingTagLabel = voucherUserReportingTagLabelByTag.get(tag) ?? tag;

                return (
                  <Chip
                    key={tag}
                    size="small"
                    variant="outlined"
                    label={reportingTagLabel}
                    title={tag}
                    clickable
                    onClick={() => handleReportingTagClick(tag)}
                    aria-label={`Include reporting tag ${reportingTagLabel}`}
                  />
                );
              })}
            </Stack>
          );
        },
      },
      {
        field: 'fkCauseId',
        headerName: 'Cause ID',
        ...sharedOpts,
        renderCell: (params) => {
          const causeId = params.row.fkCauseId ?? params.row.idCauseFk;
          if (!causeId) {
            return '—';
          }
          const isAutoAssignOrder = params.row.voucherUserReportingTags?.includes(
            VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER,
          );
          if (isAutoAssignOrder) {
            return (
              <Link to={`/purchases/${causeId}`} target="_blank" rel="noreferrer">
                {causeId}
              </Link>
            );
          }
          return (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {causeId}
            </Typography>
          );
        },
      },
      {
        field: 'debug',
        headerName: 'Debug',
        ...sharedOpts,
        renderCell: (params) => {
          const userSummary = resolvedSummaryMap.get(params.row.userId);
          return (
            <DebugModal
              type="generic"
              title="Voucher User"
              data={{
                ...params.row,
                userName: userSummary?.full_name,
                userEmail: userSummary?.email,
              }}
            />
          );
        },
      },
    ];

    if (shouldShowDeleteVoucherUserActions) {
      base.push(
        {
          field: 'expireActions',
          headerName: 'Convert to credit',
          ...sharedOpts,
          width: 150,
          flex: 0,
          hideable: false,
          disableColumnMenu: true,
          renderCell: (params) => {
            const voucherToCreditBlockReason = getVoucherToCreditBlockReason({
              voucherUser: params.row,
              voucher: resolvedVouchersMap.get(params.row.voucherId),
              isAlreadyCredited: creditedVoucherUserIds.has(params.row.voucherUserId),
              now: currentTime,
            });

            return (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PermissionedComponent requiredRoles={VOUCHER_TO_CREDIT_ROLES}>
                  <Tooltip title={voucherToCreditBlockReason ?? 'Convert voucher to credit'}>
                    <span>
                      <IconButton
                        aria-label="Convert voucher to credit"
                        size="small"
                        onClick={() => setCreditTarget(params.row)}
                        disabled={Boolean(voucherToCreditBlockReason) || expireVoucherUserActionsDisabled}
                      >
                        <CurrencyExchangeIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </PermissionedComponent>
              </Stack>
            );
          },
        },
        {
          field: 'alignExpiryAction',
          headerName: 'Align expiry',
          ...sharedOpts,
          width: 160,
          flex: 0,
          hideable: false,
          disableColumnMenu: true,
          renderCell: ({ row }) => {
            const voucherExpiresAt = resolvedVouchersMap.get(row.voucherId)?.voucherUserExpiresAt;
            const reason = getAlignVoucherUserExpiryBlockReason({
              usedAt: row.usedAt,
              expiredAt: row.expiredAt,
              voucherExpiresAt,
              now: currentTime,
            });
            return (
              <Tooltip title={reason ?? 'Set this assignment to the voucher’s expiry'}>
                <span>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    disabled={Boolean(reason) || !isEditing || modeLocked || !brand.promoBrand}
                    onClick={() => {
                      if (voucherExpiresAt) setExpiryResolutionTarget({ row, expiredAt: voucherExpiresAt });
                    }}
                  >
                    Align expiry
                  </Button>
                </span>
              </Tooltip>
            );
          },
        },
        {
          field: 'deleteAction',
          headerName: 'Delete',
          ...sharedOpts,
          width: 90,
          align: 'right',
          headerAlign: 'right',
          flex: 0,
          hideable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <IconButton
              aria-label="Delete voucher user"
              size="small"
              color="error"
              onClick={() => setExpireTarget(params.row)}
              disabled={expireVoucherUserActionsDisabled || expireVoucherUserMutation.isPending}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ),
        },
      );
    }

    return base;
  }, [
    currentTime,
    isEditing,
    modeLocked,
    brand.promoBrand,
    resolvedSummaryMap,
    resolvedVouchersMap,
    handleUserIdClick,
    handleVoucherClick,
    voucherUserReportingTagLabelByTag,
    handleReportingTagClick,
    shouldShowDeleteVoucherUserActions,
    expireVoucherUserActionsDisabled,
    expireVoucherUserMutation.isPending,
    showDateTimes,
    bulkUpdateMode,
    allVisibleSelected,
    selectedVisibleCount,
    selectedVoucherUserIds,
    isLoading,
    voucherUsers,
    bulkSelectionLocked,
    creditedVoucherUserIds,
  ]);

  const columnsForSelector = useMemo(
    () =>
      columns.filter(
        (c) =>
          c.field !== 'debug' &&
          c.field !== 'expireActions' &&
          c.field !== 'deleteAction' &&
          c.field !== 'alignExpiryAction' &&
          c.field !== 'bulkSelect',
      ),
    [columns],
  );

  const Toolbar = useCallback(
    () => (
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ p: 1, width: '100%', justifyContent: 'flex-end' }}
      >
        <FormControlLabel
          control={
            <Switch checked={extendedInfo} onChange={(_, checked) => handleExtendedInfoChange(checked)} size="small" />
          }
          label="Extended info"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showDateTimes}
              onChange={(_, checked) =>
                setPromoPreferences((current) => ({
                  ...current,
                  uiSelection: {
                    ...current.uiSelection,
                    voucherUsers: { ...current.uiSelection.voucherUsers, showDateTimes: checked },
                  },
                }))
              }
              size="small"
            />
          }
          label="Show date and time"
        />
        <ColumnSelector
          columns={columnsForSelector}
          columnVisibilityModel={mergedColumnVisibilityModel}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          size="small"
        />
      </Stack>
    ),
    [
      mergedColumnVisibilityModel,
      columnsForSelector,
      extendedInfo,
      handleColumnVisibilityChange,
      handleExtendedInfoChange,
      showDateTimes,
    ],
  );

  const voucherLookupMainSections = (
    <>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: 1 }}>
          <UserSearchWidget
            id={embedded ? 'voucher-users-search-embedded' : 'voucher-users-search'}
            label="Name / Email / Customer Support Code / User ID"
            formErrors={[]}
            onChange={handleUserSelected}
            passedAutocompleteValue={searchWidgetValue}
            setPassedAutocompleteValue={setSearchWidgetValue}
          />
        </Box>
        {!hasDirectVoucherId && (
          <TextField
            label="Voucher Name"
            size="small"
            value={filterVoucherName}
            onChange={(e) => {
              setFilterVoucherName(e.target.value);
              setSelectedMatchedVoucherId(undefined);
              setPageNum(0);
            }}
            sx={{ minWidth: 220 }}
          />
        )}
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="voucher-user-status-filter-label">Voucher status</InputLabel>
          <Select<Array<string>>
            multiple
            labelId="voucher-user-status-filter-label"
            label="Voucher status"
            value={selectedStatusOptions}
            renderValue={(selected) =>
              VOUCHER_USER_STATUS_OPTIONS.filter((option) => selected.includes(option.value))
                .map((option) => option.label)
                .join(', ')
            }
            onChange={(event) => {
              const statuses =
                typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
              if (!statuses.includes('unused') && !statuses.includes('used')) {
                return;
              }
              setFilterOnlyUsed(!statuses.includes('unused'));
              setFilterIncludeUsed(statuses.includes('used'));
              setFilterIncludeExpired(statuses.includes('expired'));
              setPageNum(0);
            }}
          >
            {VOUCHER_USER_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedStatusOptions.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Select unused, used, or both. Expired vouchers are optional.</FormHelperText>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={showAdvancedFilters}
              onChange={(_, checked) => setShowAdvancedFilters(checked)}
              inputProps={{ 'aria-controls': 'voucher-users-advanced-filters' }}
            />
          }
          label="Advanced"
        />
        <Button variant="text" onClick={handleClear}>
          Clear
        </Button>
      </Stack>
      <Collapse in={showAdvancedFilters} id="voucher-users-advanced-filters">
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            {[
              {
                label: 'Include reporting tags',
                value: filterReportingTags,
                setValue: setFilterReportingTags,
                color: 'primary' as const,
              },
              {
                label: 'Exclude reporting tags',
                value: filterExcludedReportingTags,
                setValue: setFilterExcludedReportingTags,
                color: 'warning' as const,
              },
            ].map(({ label, value, setValue, color }) => (
              <Autocomplete
                key={label}
                multiple
                freeSolo
                size="small"
                options={reportingTagMetaOptions.map((option) => option.value)}
                getOptionLabel={(tag) => voucherUserReportingTagLabelByTag.get(tag) ?? tag}
                value={value}
                onChange={(_, tags) => {
                  setValue(Array.from(new Set(tags.map((tag) => tag.trim()).filter(Boolean))));
                  setPageNum(0);
                }}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={tag}
                      size="small"
                      variant="outlined"
                      color={color}
                      label={voucherUserReportingTagLabelByTag.get(tag) ?? tag}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    helperText={
                      label.startsWith('Exclude')
                        ? 'Excludes rows matching any selected tag'
                        : 'Includes rows matching any selected tag'
                    }
                  />
                )}
                sx={{ minWidth: 240, flex: 1 }}
              />
            ))}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filterExpiresAfterVoucher}
                  onChange={(_, checked) => {
                    setFilterExpiresAfterVoucher(checked);
                    setPageNum(0);
                  }}
                />
              }
              label={
                <Tooltip title="Only assignments whose expiry is after the voucher’s configured user expiry date. Both dates must be set.">
                  <Typography variant="body2">Expires after voucher expiry</Typography>
                </Tooltip>
              }
            />
            <TextField
              label="Created by (user ID)"
              size="small"
              value={filterCreatedBy}
              onChange={(event) => {
                setFilterCreatedBy(event.target.value.trim());
                setPageNum(0);
              }}
            />
            {[
              { label: 'Updated after', value: filterUpdatedAfter, setValue: setFilterUpdatedAfter },
              { label: 'Updated before', value: filterUpdatedBefore, setValue: setFilterUpdatedBefore },
            ].map(({ label, value, setValue }) => (
              <TextField
                key={label}
                label={label}
                type="datetime-local"
                size="small"
                value={value}
                error={Boolean(value && !dayjs(value).isValid())}
                helperText={value && !dayjs(value).isValid() ? 'Enter a valid date and time' : undefined}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => {
                  setValue(event.target.value);
                  setPageNum(0);
                }}
              />
            ))}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="voucher-user-sort-field-label">Sort by</InputLabel>
              <Select
                labelId="voucher-user-sort-field-label"
                label="Sort by"
                value={displayedSortModel[0]?.field ?? 'expiredAt'}
                onChange={(event) => handleSortFieldChange(event.target.value)}
              >
                {voucherUserSortOptions.map((option) => (
                  <MenuItem key={option.field} value={option.field}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="voucher-user-sort-direction-label">Direction</InputLabel>
              <Select
                labelId="voucher-user-sort-direction-label"
                label="Direction"
                value={displayedSortModel[0]?.sort ?? DEFAULT_VOUCHER_USER_SORT_DIRECTION}
                onChange={(event) => handleSortDirectionChange(event.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Collapse>
      {filterReportingTags.length > 0 || filterExcludedReportingTags.length > 0 ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Active filters:
          </Typography>
          {[
            { tags: filterReportingTags, setTags: setFilterReportingTags, label: 'Include', color: 'primary' as const },
            {
              tags: filterExcludedReportingTags,
              setTags: setFilterExcludedReportingTags,
              label: 'Exclude',
              color: 'warning' as const,
            },
          ].flatMap(({ tags, setTags, label, color }) =>
            tags.map((tag) => (
              <Chip
                key={`${label}-${tag}`}
                label={`${label}: ${voucherUserReportingTagLabelByTag.get(tag) ?? tag}`}
                size="small"
                color={color}
                variant="outlined"
                onDelete={() => {
                  setTags((current) => current.filter((value) => value !== tag));
                  setPageNum(0);
                }}
              />
            )),
          )}
          {reportingTagsFilterModeLabel ? (
            <Button
              size="small"
              variant="text"
              onClick={handleReportingTagsFilterModeToggle}
              sx={{ minWidth: 0, minHeight: 24, px: 0.5 }}
            >
              {reportingTagsFilterModeLabel}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      {needsDisambiguation && matchedVouchersData?.result?.vouchers && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Multiple vouchers match &ldquo;{filterVoucherName}&rdquo;</AlertTitle>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {matchedVouchersData.result.vouchers.map((v) => (
              <Chip
                key={v.voucherId}
                label={v.voucherName}
                onClick={() => setSelectedMatchedVoucherId(v.voucherId)}
                variant="outlined"
                clickable
              />
            ))}
          </Stack>
        </Alert>
      )}

      {resolvedVoucher && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Stack direction="row" spacing={4} alignItems="flex-start" flexWrap="wrap">
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">
                Voucher
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                <Link to={`/marketing/vouchers/${resolvedVoucher.voucherId}`}>{resolvedVoucher.voucherName}</Link>
              </Typography>
              {resolvedVoucher.voucherTitle && resolvedVoucher.voucherTitle !== resolvedVoucher.voucherName && (
                <Typography variant="body2" color="text.secondary">
                  {resolvedVoucher.voucherTitle}
                </Typography>
              )}
            </Box>

            {resolvedVoucher.voucherDescription && (
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">{resolvedVoucher.voucherDescription}</Typography>
              </Box>
            )}

            {resolvedVoucher.voucherRewards && resolvedVoucher.voucherRewards.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Rewards
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {resolvedVoucher.voucherRewards.map((r) => (
                    <Chip
                      key={r.id_voucher_reward}
                      size="small"
                      label={
                        r.currency_code ? currencyFormatter(r.currency_code, r.reward_amount) : `${r.reward_amount}`
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box>
                <Chip
                  size="small"
                  color={resolvedVoucher.deactivatedAt ? 'error' : 'success'}
                  label={resolvedVoucher.deactivatedAt ? 'Deactivated' : 'Active'}
                />
              </Box>
            </Box>

            {resolvedVoucher.voucherToggles.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Toggles
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {resolvedVoucher.voucherToggles.map((t) => (
                    <Chip key={t} size="small" variant="outlined" label={t} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {controlledUserMode === undefined && (
        <Box sx={{ mt: 2 }}>
          <PromoFeature
            presentation="radioSelect"
            label="User mode"
            value={isEditing ? userMode : 'read-only'}
            disabled={modeLocked}
            disabledReason="Finish the current voucher user operation before changing modes."
            onChange={(mode) => {
              setLocalUserMode(mode);
              setSelectedVoucherUsers([]);
              setExpireTarget(null);
            }}
            options={[
              { value: 'read-only', label: 'Read-only' },
              {
                value: 'edit',
                label: 'Edit',
                featureKey: 'voucher',
                disabled: expireVoucherUserActionsDisabled,
                disabledReason: 'Editing is temporarily disabled by the voucher form.',
              },
              {
                value: 'bulk-edit',
                label: 'Bulk-edit',
                featureKey: 'voucher-user-bulk-remove',
                disabled: expireVoucherUserActionsDisabled,
                disabledReason: 'Editing is temporarily disabled by the voucher form.',
              },
            ]}
          />
        </Box>
      )}
      {bulkUpdateMode && canBulkRemoveVoucherUsers && (
        <VoucherUserBulkActions
          voucherExpiry={
            resolvedVoucherId ? resolvedVouchersMap.get(resolvedVoucherId)?.voucherUserExpiresAt : undefined
          }
          selected={selectedVoucherUsers}
          matchingQuery={queryParams}
          matchingCount={totalCount}
          hasMatchingFilters={Boolean(
            queryParams.user_id ||
            queryParams.voucher_id ||
            queryParams.voucherUserReportingTags?.length ||
            queryParams.voucherUserReportingTagsExclude?.length ||
            queryParams.isExpiringAfterVoucherExpiryAt ||
            queryParams.createdBy ||
            queryParams.updatedAfter ||
            queryParams.updatedBefore ||
            filterIncludeExpired !== defaultIncludeExpired ||
            !filterIncludeUsed ||
            filterOnlyUsed,
          )}
          matchingReady={
            !isLoading &&
            !isFetching &&
            !isPlaceholderData &&
            !isError &&
            !(isVoucherNameFilterActive && (!hasResolvedVouchers || needsDisambiguation))
          }
          batchSize={pageSize}
          onSelectionLockChange={setBulkSelectionLocked}
          onClearSelection={() => setSelectedVoucherUsers([])}
          onFinished={async () => {
            await queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
            if (resolvedVoucherId) {
              await queryClient.invalidateQueries({ queryKey: ['voucher-users', resolvedVoucherId] });
            }
          }}
        />
      )}
      <Box mt={1}>
        <DataGrid
          slots={{ pagination: GridPagination, toolbar: Toolbar }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={voucherUsers}
          columns={columns}
          autoHeight
          getRowHeight={() => 'auto'}
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          getRowId={(row) => row.voucherUserId}
          paginationModel={{ page: pageNum, pageSize }}
          columnVisibilityModel={mergedColumnVisibilityModel}
          onColumnVisibilityModelChange={handleGridColumnVisibilityModelChange}
          sortModel={displayedSortModel}
          onSortModelChange={handleSortModelChange}
          sx={{ backgroundColor: 'background.paper' }}
        />
      </Box>

      {filterUserId && !isPromoUserFilterLinked && (
        <VoucherRecentRequests
          voucherName={filterVoucherName}
          userId={filterUserId}
          brand={tenant.tenant.brand}
          promoMeta={promoMeta}
          showVoucherLogsAccordion={false}
        />
      )}
    </>
  );

  const voucherLogsSection = (
    <VoucherLogsTable
      queryOptions={voucherLogsQueryOptions}
      onQueryOptionsChange={setVoucherLogsQuery}
      onUserIdClick={handleUserIdClick}
      onVoucherClick={handleVoucherClick}
      flushTop={embedded}
    />
  );

  const content = (
    <Box>
      {embedded ? (
        <>
          <Accordion
            defaultExpanded={defaultAccordionsOpen}
            disableGutters
            elevation={0}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{`Existing Users [${voucherUsers.length} of ${totalCount}]`}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>{voucherLookupMainSections}</AccordionDetails>
          </Accordion>

          {!hideVoucherLogs && voucherLogsSection}
        </>
      ) : (
        <>
          {showUsersAccordion ? (
            <Accordion
              id="voucher-users"
              defaultExpanded={defaultAccordionsOpen}
              disableGutters
              elevation={0}
              sx={{
                mt: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{`Existing Users [${voucherUsers.length} of ${totalCount}]`}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>{voucherLookupMainSections}</AccordionDetails>
            </Accordion>
          ) : (
            <>
              <PageSubheader title={`Existing Users [${voucherUsers.length} of ${totalCount}]`} />
              {voucherLookupMainSections}
            </>
          )}
          {!hideVoucherLogs && (
            <Accordion
              defaultExpanded={defaultAccordionsOpen}
              disableGutters
              elevation={0}
              sx={{
                mt: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{`Voucher logs [${voucherLogsPageRowCount} of ${voucherLogsTotalCount}]`}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>{voucherLogsSection}</AccordionDetails>
            </Accordion>
          )}
        </>
      )}
      <Dialog
        open={Boolean(expiryResolutionTarget)}
        onClose={() => {
          if (!updateExpiryMutation.isPending) setExpiryResolutionTarget(null);
        }}
      >
        <DialogTitle>Resolve voucher user expiry</DialogTitle>
        <DialogContent>
          {expiryResolutionTarget && (
            <Stack spacing={2}>
              <Typography>
                Update this assignment from{' '}
                {expiryResolutionTarget.row.expiredAt
                  ? dayjs(expiryResolutionTarget.row.expiredAt).format('D MMM YYYY, h:mm a')
                  : 'no expiry'}{' '}
                to {dayjs(expiryResolutionTarget.expiredAt).format('D MMM YYYY, h:mm a')}, matching the voucher&apos;s
                configured user expiry.
              </Typography>
              {dayjs(expiryResolutionTarget.expiredAt).isBefore(dayjs()) && (
                <Alert severity="warning">
                  The expiry API requires a future date. This assignment cannot be resolved to the configured date.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={updateExpiryMutation.isPending} onClick={() => setExpiryResolutionTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !isEditing ||
              updateExpiryMutation.isPending ||
              !expiryResolutionTarget ||
              !dayjs(expiryResolutionTarget.expiredAt).isAfter(dayjs())
            }
            onClick={handleResolveExpiry}
          >
            {updateExpiryMutation.isPending ? 'Updating...' : 'Update expiry'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(expireTarget)} onClose={() => setExpireTarget(null)}>
        <DialogTitle>Are you sure you want to delete this voucher user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove voucher {expireTargetVoucherName} for user {expireTargetUserName}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setExpireTarget(null)} disabled={expireVoucherUserMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleExpireConfirm}
            disabled={!isEditing || expireVoucherUserMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {creditTarget && (
        <VoucherToCreditDialog
          key={creditTarget.voucherUserId}
          open
          onClose={() => setCreditTarget(null)}
          onCreditAdded={(voucherUserId) =>
            setCreditedVoucherUserIds((previous) => new Set(previous).add(voucherUserId))
          }
          onVoucherUsersChanged={refreshVoucherUsers}
          voucherUserId={creditTarget.voucherUserId}
          voucherId={creditTarget.voucherId}
          userId={creditTarget.userId}
          brand={tenant.tenant.brand}
          userLabel={creditTargetUserSummary?.email ?? creditTargetUserSummary?.full_name ?? creditTarget.userId}
          voucherName={creditTargetVoucher?.voucherName ?? creditTarget.voucherId}
          voucherCurrency={creditTargetVoucher?.voucherRewards?.find((reward) => reward.currency_code)?.currency_code}
        />
      )}
      {!hideFooterDebug && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <DebugModal type="generic" title="Voucher users search page debug" data={voucherUsersSearchPageDebugData} />
        </Box>
      )}
    </Box>
  );

  if (hasDirectVoucherId || embedded || showUsersAccordion) {
    return content;
  }

  return (
    <Container sx={{ width: '100%' }}>
      <Helmet>
        <title>Voucher Lookup</title>
      </Helmet>
      {content}
    </Container>
  );
}
